import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Empty, Input, Modal, Popconfirm, Row, Select, Space, Spin, Table, Tabs, Tag, Tooltip, Typography, Upload, message } from 'antd';
import { ArrowLeftOutlined, CalendarOutlined, DeleteOutlined, DownloadOutlined, EditOutlined, EnvironmentOutlined, EyeOutlined, FilePdfOutlined, FlagOutlined, GlobalOutlined, HomeOutlined, InboxOutlined, MailOutlined, MobileOutlined, PhoneOutlined, SearchOutlined, SwapOutlined, UploadOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { ListFournisService } from '../../Services/ListFournisService';
import { FactureService } from '../../Services/FactureService';
import { TraitementService } from '../../Services/TraitementService';
import * as XLSX from 'xlsx';
import printJS from 'print-js';
import '../../Pages/CSS/DonnesBases.css';

const { Title } = Typography;
const { Dragger } = Upload;

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('fr-FR');
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
  return new Intl.NumberFormat('fr-FR').format(Number(value));
};

const displayOrDash = (value) => value || '-';

const etatConfig = {
  R: { label: 'Recue', color: 'blue' },
  T: { label: 'Traitee', color: 'orange' },
  C: { label: 'Comptabilisee', color: 'geekblue' },
  P: { label: 'Payee', color: 'green' },
};

const toTimestamp = (value) => {
  if (!value) return Number.NEGATIVE_INFINITY;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? Number.NEGATIVE_INFINITY : date.getTime();
};

const InfoRow = ({ icon, label, value }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        flexShrink: 0,
        borderRadius: 8,
        background: '#e6f4ff',
        color: '#1677ff',
        fontSize: 14,
      }}
    >
      {icon}
    </div>
    <div style={{ lineHeight: 1.3, minWidth: 0 }}>
      <div style={{ fontSize: 11, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: 0.3 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, fontWeight: 500, wordBreak: 'break-word' }}>{value}</div>
    </div>
  </div>
);

const sortableTitle = (label) => (
  <Space size={4}>
    <span>{label}</span>
    <SwapOutlined style={{ fontSize: 11, color: '#8c8c8c' }} />
  </Space>
);

const getYearFromDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    const match = String(value).match(/(19|20)\d{2}/);
    return match ? match[0] : null;
  }
  return String(date.getFullYear());
};

const ViewFournisseurPage = () => {
  const navigate = useNavigate();
  const { numFourn, numfourn } = useParams();
  const fournisseurId = numFourn || numfourn;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fournisseur, setFournisseur] = useState(null);
  const [exerciceOptions, setExerciceOptions] = useState([]);
  const [selectedExercice, setSelectedExercice] = useState(String(new Date().getFullYear()));
  const [visibleFactures, setVisibleFactures] = useState([]);
  const [fournisseurFiles, setFournisseurFiles] = useState([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState(null);

  // États pour les actions sur facture numérisée
  const [uploadingFactureId, setUploadingFactureId] = useState(null);
  const [deletingFactureDocId, setDeletingFactureDocId] = useState(null);
  const [loadingPreviewId, setLoadingPreviewId] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewTitle, setPreviewTitle] = useState('');

  const listFournisService = useMemo(() => new ListFournisService(), []);
  const factureService = useMemo(() => new FactureService(), []);
  const traitementService = useMemo(() => new TraitementService(), []);

  const fetchFournisseur = useCallback(async () => {
    if (!fournisseurId) {
      setError('Parametre numfourn introuvable dans la route.');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const data = await listFournisService.getOneListFournis(fournisseurId);
      setFournisseur(data);
    } catch (err) {
      console.error('Erreur lors du chargement du fournisseur :', err);
      setError('Impossible de charger les informations du fournisseur.');
    } finally {
      setIsLoading(false);
    }
  }, [fournisseurId, listFournisService]);

  useEffect(() => {
    fetchFournisseur();
  }, [fetchFournisseur]);

  const fetchExercices = useCallback(async () => {
    if (!fournisseurId) return;
    try {
      const exercices = await factureService.getListExercicesFactureByFourn(fournisseurId);
      const normalized = Array.from(
        new Set(
          (Array.isArray(exercices) ? exercices : [])
            .map((item) => String(item))
            .filter((item) => item && item !== 'null' && item !== 'undefined')
        )
      );
      normalized.sort((a, b) => Number(b) - Number(a));
      setExerciceOptions(normalized);
    } catch (err) {
      console.error('Erreur lors du chargement des exercices fournisseur :', err);
    }
  }, [fournisseurId, factureService]);

  useEffect(() => {
    fetchExercices();
  }, [fetchExercices]);

  const fetchFournisseurFiles = useCallback(async () => {
    if (!fournisseurId) return;
    try {
      const filesResponse = await listFournisService.getAllFournFiles(fournisseurId);
      const files = Array.isArray(filesResponse)
        ? filesResponse
        : filesResponse?.data || filesResponse?.list || filesResponse?.files || filesResponse?.content || [];
      setFournisseurFiles(Array.isArray(files) ? files : []);
    } catch (err) {
      console.error('Erreur lors du chargement des fichiers fournisseur :', err);
      setFournisseurFiles([]);
    }
  }, [fournisseurId, listFournisService]);

  useEffect(() => {
    fetchFournisseurFiles();
  }, [fetchFournisseurFiles]);

  const handleUploadFournFiles = async () => {
    if (!selectedFiles.length) {
      message.warning('Veuillez selectionner au moins un fichier.');
      return false;
    }
    try {
      setUploading(true);
      const filesToUpload = selectedFiles.map((f) => f.originFileObj).filter(Boolean);
      await listFournisService.uploadFournFiles(fournisseurId, filesToUpload);
      message.success('Fichier(s) televerse(s) avec succes.');
      setSelectedFiles([]);
      await fetchFournisseurFiles();
      return true;
    } catch (err) {
      console.error('Erreur upload :', err);
      message.error('Echec du televersement des documents.');
      return false;
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadFile = async (file) => {
    try {
      const isPdf = file.file_type === 'application/pdf' || /\.pdf$/i.test(file.file_name || '');
      const response = await listFournisService.downloadFileFourn(file.file_id);

      if (isPdf) {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const fileURL = window.URL.createObjectURL(blob);
        printJS(fileURL);
        return;
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.file_name || `fichier_${file.file_id}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erreur telechargement :', err);
      message.error('Impossible de telecharger le fichier.');
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      setDeletingFileId(fileId);
      await listFournisService.deleteFournFile(fileId);
      message.success('Fichier supprime avec succes.');
      await fetchFournisseurFiles();
    } catch (err) {
      console.error('Erreur suppression :', err);
      message.error('Impossible de supprimer le fichier.');
    } finally {
      setDeletingFileId(null);
    }
  };

  const MAX_FACTURE_DOC_SIZE = 800 * 1024;

  const handleUploadFactureDoc = async (record, file) => {
    if (file.type !== 'application/pdf') {
      message.error('Seuls les fichiers PDF sont autorisés.');
      return;
    }
    if (file.size > MAX_FACTURE_DOC_SIZE) {
      message.error('Le fichier ne doit pas dépasser 800 Ko.');
      return;
    }
    const factureId = record.numenreg;
    try {
      setUploadingFactureId(factureId);
      const formData = new FormData();
      formData.append('facture', file);
      await traitementService.ImportFile(factureId, formData);
      message.success('Facture numérisée téléversée avec succès.');
      await fetchFournisseur();
    } catch (err) {
      console.error('Erreur upload facture numérisée :', err);
      message.error('Echec du téléversement de la facture numérisée.');
    } finally {
      setUploadingFactureId(null);
    }
  };

  const handleViewFactureDoc = (record) => {
    traitementService.printFactureNumeriseeService(record.numenreg);
  };

  const handleClosePreview = () => {
    setPreviewVisible(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const handleDeleteFactureDoc = async (record) => {
    try {
      setDeletingFactureDocId(record.numenreg);
      await factureService.deleteFileFromFileSystem(record.numenreg);
      message.success('Facture numérisée supprimée.');
      await fetchFournisseur();
    } catch (err) {
      console.error('Erreur suppression facture numérisée :', err);
      message.error('Impossible de supprimer la facture numérisée.');
    } finally {
      setDeletingFactureDocId(null);
    }
  };

  const listFactures = useMemo(
    () => (Array.isArray(fournisseur?.listFactures) ? fournisseur.listFactures : []),
    [fournisseur?.listFactures]
  );


  const facturesByExercice = useMemo(() => {
    if (!selectedExercice || selectedExercice === 'TOUT') return listFactures;
    return listFactures.filter((item) => getYearFromDate(item?.datesaisie) === String(selectedExercice));
  }, [listFactures, selectedExercice]);

  const exerciceSelectOptions = useMemo(() => {
    const systemYear = String(new Date().getFullYear());
    const merged = Array.from(new Set([systemYear, ...exerciceOptions]));
    return [
      { label: 'Tout', value: 'TOUT' },
      ...merged.map((ex) => ({ label: ex, value: ex })),
    ];
  }, [exerciceOptions]);

  useEffect(() => {
    setVisibleFactures(facturesByExercice);
  }, [facturesByExercice]);

  const totalMontantVisible = useMemo(
    () => visibleFactures.reduce((sum, item) => sum + Number(item?.montfact || 0), 0),
    [visibleFactures]
  );

  const googleAddressQuery = useMemo(() => {
    const query = [fournisseur?.raisoc, fournisseur?.adresse, fournisseur?.region, fournisseur?.pays]
      .filter(Boolean)
      .join(', ')
      .trim();

    return query || null;
  }, [fournisseur?.raisoc, fournisseur?.adresse, fournisseur?.region, fournisseur?.pays]);

  const googleMapSrc = useMemo(() => {
    if (!googleAddressQuery) return null;
    return `https://www.google.com/maps?q=${encodeURIComponent(googleAddressQuery)}&z=15&output=embed`;
  }, [googleAddressQuery]);

  const exportFacturesToExcel = () => {
    if (!visibleFactures.length) {
      message.info('Aucune facture a exporter.');
      return;
    }

    const allKeys = Array.from(
      new Set(visibleFactures.flatMap((item) => Object.keys(item || {})))
    ).filter((key) => key !== 'listDoc');

    const rows = visibleFactures.map((item) => {
      const row = {};
      allKeys.forEach((key) => {
        const value = item?.[key];
        row[key] = value !== null && typeof value === 'object' ? JSON.stringify(value) : value;
      });
      return row;
    });

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Factures');
    XLSX.writeFile(workbook, `Factures_${fournisseurId || 'fournisseur'}.xlsx`);
  };

  const factureColumns = [
    {
      title: 'N° Facture',
      key: 'numenreg',
      align: 'center',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Rechercher numenreg"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block', width: 180 }}
          />
          <Space>
            <Button type="primary" size="small" icon={<SearchOutlined />} onClick={() => confirm()}>
              Rechercher
            </Button>
            <Button
              size="small"
              onClick={() => {
                clearFilters?.();
                confirm({ closeDropdown: true });
              }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
      ),
      onFilter: (value, record) =>
        String(record?.numenreg || '')
          .toLowerCase()
          .includes(String(value).toLowerCase()),
      render: (_, record, index) => record?.numenreg || `${record?.numfact || 'fact'}-${index}`,
    },
    {
      title: sortableTitle('Date F.'),
      dataIndex: 'datefact',
      key: 'datefact',
      align: 'center',
      sorter: (a, b) => toTimestamp(a?.datefact) - toTimestamp(b?.datefact),
      render: (value) => formatDate(value),
    },
    {
      title: sortableTitle('Reçue le'),
      dataIndex: 'daterecep',
      key: 'daterecep',
      align: 'center',
      sorter: (a, b) => toTimestamp(a?.daterecep) - toTimestamp(b?.daterecep),
      render: (value) => formatDate(value),
    },
    {
      title: sortableTitle('Saisie le'),
      dataIndex: 'datesaisie',
      key: 'datesaisie',
      align: 'center',
      sorter: (a, b) => toTimestamp(a?.datesaisie) - toTimestamp(b?.datesaisie),
      render: (value) => formatDate(value),
    },
    {
      title: 'État',
      dataIndex: 'etat',
      key: 'etat',
      align: 'center',
      filters: Object.keys(etatConfig).map((key) => ({
        text: etatConfig[key].label,
        value: key,
      })),
      onFilter: (value, record) => record?.etat === value,
      render: (value) => {
        const config = etatConfig[value] || null;
        if (!config) return displayOrDash(value);
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: 'N° facture Frs',
      dataIndex: 'numfact',
      key: 'numfact',
      render: (value) => displayOrDash(value),
    },
    {
      title: 'Montant',
      dataIndex: 'montfact',
      key: 'montfact',
      align: 'right',
      render: (value) => <strong>{formatCurrency(value)}</strong>,
    },
    {
      title: sortableTitle('Date compta'),
      dataIndex: 'datecompta',
      key: 'datecompta',
      align: 'center',
      sorter: (a, b) => toTimestamp(a?.datecompta) - toTimestamp(b?.datecompta),
      render: (value) => formatDate(value),
    },
    {
      title: 'Date paiement',
      dataIndex: 'datereglmt',
      key: 'datereglmt',
      align: 'center',
      render: (value) => formatDate(value),

    },
    {
      title: 'Traitee par',
      key: 'traiteePar',
      filters: Array.from(
        new Set(
          facturesByExercice
            .map((item) => displayOrDash(item?.compta_by || item?.process_by || item?.created_by))
            .filter((value) => value && value !== '-')
        )
      ).map((value) => ({ text: value, value })),
      onFilter: (value, record) =>
        (record?.compta_by || record?.process_by || record?.created_by) === value,
      render: (_, record) => displayOrDash(record?.compta_by || record?.process_by || record?.created_by),
    },
    {
      title: 'Facture numérisée',
      key: 'factureDoc',
      align: 'center',
      width: 130,
      fixed: 'right',
      render: (_, record) => {
        const numerisee = !!record.file_path;
        const isUploading = uploadingFactureId === record.numenreg;
        const isDeleting = deletingFactureDocId === record.numenreg;
        const isPreviewing = loadingPreviewId === record.numenreg;

        return (
          <Space size={4}>
            <Tooltip title={numerisee ? 'Remplacer la facture PDF' : 'Uploader la facture PDF'}>
              <Upload
                accept="application/pdf"
                beforeUpload={(file) => { handleUploadFactureDoc(record, file); return false; }}
                showUploadList={false}
              >
                <Button
                  type="link"
                  size="small"
                  loading={isUploading}
                  icon={<UploadOutlined style={{ fontSize: 16, color: numerisee ? '#faad14' : '#1677ff' }} />}
                />
              </Upload>
            </Tooltip>

            {numerisee && (
              <Tooltip title="Visualiser la facture PDF">
                <Button
                  type="link"
                  size="small"
                  loading={isPreviewing}
                  onClick={() => handleViewFactureDoc(record)}
                  icon={<EyeOutlined style={{ fontSize: 16, color: '#52c41a' }} />}
                />
              </Tooltip>
            )}

            {numerisee && (
              <Tooltip title="Supprimer la facture PDF">
                <Popconfirm
                  title="Supprimer la facture numérisée ?"
                  description="Cette action est irréversible."
                  okText="Supprimer"
                  okButtonProps={{ danger: true }}
                  cancelText="Annuler"
                  onConfirm={() => handleDeleteFactureDoc(record)}
                >
                  <Button
                    type="link"
                    size="small"
                    loading={isDeleting}
                    icon={<DeleteOutlined style={{ fontSize: 16, color: '#ff4d4f' }} />}
                  />
                </Popconfirm>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  const normalizedFournisseurFiles = useMemo(() =>
    fournisseurFiles.map((file) => ({
      key: file.file_id,
      file_id: file.file_id,
      file_name: file.file_name || '-',
      file_type: file.file_type || '-',
      file_size: file.file_size ?? null,
    }))
    , [fournisseurFiles]);

  const formatMime = (mime) => {
    if (!mime) return '-';
    const map = {
      'application/pdf': 'PDF',
      'application/msword': 'DOC',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
      'application/vnd.ms-excel': 'XLS',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
      'application/vnd.ms-powerpoint': 'PPT',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
      'application/zip': 'ZIP',
      'application/x-zip-compressed': 'ZIP',
      'application/x-rar-compressed': 'RAR',
      'application/x-7z-compressed': '7Z',
      'text/plain': 'TXT',
      'text/csv': 'CSV',
      'text/xml': 'XML',
      'application/xml': 'XML',
      'application/json': 'JSON',
      'image/jpeg': 'JPG',
      'image/png': 'PNG',
      'image/gif': 'GIF',
      'image/webp': 'WEBP',
      'image/svg+xml': 'SVG',
      'image/tiff': 'TIFF',
      'image/bmp': 'BMP',
    };
    if (map[mime]) return map[mime];
    const sub = mime.split('/')[1] || mime;
    return sub.replace(/^(x-|vnd\.)/, '').split('.').pop().toUpperCase();
  };

  const fournisseurFileColumns = [
    {
      title: 'N°',
      dataIndex: 'file_id',
      key: 'file_id',
      align: 'center',
      width: 80,
    },
    {
      title: 'Nom du fichier',
      dataIndex: 'file_name',
      key: 'file_name',
    },
    {
      title: 'Type',
      dataIndex: 'file_type',
      key: 'file_type',
      align: 'center',
      width: 100,
      render: (value) => formatMime(value),
    },
    {
      title: 'Taille (Ko)',
      dataIndex: 'file_size',
      key: 'file_size',
      align: 'right',
      width: 110,
      render: (value) => (value !== null ? value : '-'),
    },
    {
      title: '',
      key: 'actions',
      align: 'center',
      width: 90,
      render: (_, record) => (
        <Space size={12}>
          <Button
            type="link"
            icon={<DownloadOutlined style={{ color: '#52c41a', fontSize: 16 }} />}
            size="small"
            onClick={() => handleDownloadFile(record)}
          />
          <Popconfirm
            title="Supprimer ce fichier ?"
            okText="Oui"
            cancelText="Non"
            onConfirm={() => handleDeleteFile(record.file_id)}
          >
            <Button
              type="link"
              icon={<DeleteOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />}
              size="small"
              loading={deletingFileId === record.file_id}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 10 }}>
      <Spin spinning={isLoading}>
        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            style={{ marginBottom: 12 }}
          />
        )}

        <Card style={{ background: '#f3f3f3', borderRadius: 6 }} bodyStyle={{ padding: '8px 12px' }}>
          <Space align="start" style={{ width: '100%' }}>
            <UserOutlined style={{ fontSize: 22, marginTop: 2 }} />
            <Title level={5} style={{ margin: 0, lineHeight: 1.2 }}>
              {displayOrDash(fournisseur?.numFourn || fournisseurId)} - {displayOrDash(fournisseur?.raisoc)} : {displayOrDash(fournisseur?.intitule)}
            </Title>
          </Space>

          <Row gutter={[16, 16]} align="stretch">
            <Col xs={24} lg={8} style={{ display: 'flex' }}>
              <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <Space direction="vertical" size={12} style={{ width: '100%', flex: 1 }}>
                  <InfoRow icon={<HomeOutlined />} label="Adresse" value={displayOrDash(fournisseur?.adresse)} />
                  <InfoRow icon={<PhoneOutlined />} label="Telephone Fixe" value={displayOrDash(fournisseur?.telFix)} />
                  <InfoRow icon={<MobileOutlined />} label="Contact Mobile" value={displayOrDash(fournisseur?.portable)} />
                  <InfoRow icon={<MailOutlined />} label="Email" value={displayOrDash(fournisseur?.email)} />
                  <InfoRow icon={<EnvironmentOutlined />} label="Region" value={displayOrDash(fournisseur?.region)} />
                  <InfoRow icon={<FlagOutlined />} label="Pays" value={displayOrDash(fournisseur?.pays)} />
                  <InfoRow icon={<GlobalOutlined />} label="Site Web" value={displayOrDash(fournisseur?.siteWeb)} />
                  <InfoRow icon={<CalendarOutlined />} label="Date de Creation" value={formatDate(fournisseur?.dateCreation)} />
                </Space>
                <Space style={{ marginTop: 14 }}>
                  <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate(-1)}
                    style={{ borderRadius: 10 }}
                  >
                    Retour
                  </Button>
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => navigate(`/fournisseurs/edit/${fournisseur?.numFourn || fournisseurId}`)}
                    style={{ borderRadius: 10 }}
                  >
                    Modifier
                  </Button>
                </Space>
              </div>
            </Col>

            <Col xs={24} lg={16} style={{ display: 'flex' }}>
              <Card
                size="small"
                title={<Space size={6}><EnvironmentOutlined style={{ color: '#1677ff' }} /> Geolocalisation</Space>}
                bodyStyle={{ padding: 8, flex: 1, display: 'flex' }}
                style={{ borderRadius: 8, width: '100%', minHeight: 260, display: 'flex', flexDirection: 'column' }}
              >
                {googleMapSrc ? (
                  <div style={{ position: 'relative', flex: 1, width: '100%' }}>
                    <iframe
                      title="Map fournisseur"
                      src={googleMapSrc}
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, borderRadius: 6 }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                ) : (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="Coordonnees geographiques indisponibles"
                    />
                  </div>
                )}
              </Card>
            </Col>
          </Row>

        </Card>

        <Card style={{ marginTop: 12 }} bodyStyle={{ paddingTop: 8 }}>
          <Tabs
            defaultActiveKey="1"
            items={[
              {
                key: '1',
                label: `Liste des factures (${visibleFactures.length})`,
                children: (
                  <Space direction="vertical" style={{ width: '100%' }} size={10}>
                    <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
                      <strong>Total factures : {formatCurrency(totalMontantVisible)}</strong>
                      <Space>
                        <Select
                          placeholder="Exercice"
                          value={selectedExercice}
                          onChange={setSelectedExercice}
                          options={exerciceSelectOptions}
                          size="small"
                          allowClear={false}
                          style={{ width: 140, borderRadius: 10 }}
                        />
                        <Button
                          icon={<DownloadOutlined />}
                          onClick={exportFacturesToExcel}
                          size="small"
                          style={{
                            borderRadius: 10,
                            fontWeight: 600,
                            backgroundColor: '#52c41a',
                            borderColor: '#52c41a',
                            color: '#fff',
                          }}
                        >
                          Export Excel
                        </Button>
                      </Space>
                    </Space>
                    <Table
                      rowKey={(record, index) => record?.numenreg || `${record?.numfact || 'fact'}-${index}`}
                      columns={factureColumns}
                      dataSource={facturesByExercice}
                      className="custom-header1-table compact-rows-table"
                      size="small"
                      bordered
                      showSorterTooltip={false}
                      onChange={(_, __, ___, extra) => setVisibleFactures(extra.currentDataSource || facturesByExercice)}
                      pagination={{ pageSize: 5, showSizeChanger: false }}
                      scroll={{ x: 'max-content' }}
                      locale={{ emptyText: 'Aucune facture disponible pour ce fournisseur.' }}
                    />
                  </Space>
                ),
              },
              {
                key: '2',
                label: `Documents Administratif (${normalizedFournisseurFiles.length})`,
                children: (
                  <Space direction="vertical" style={{ width: '100%' }} size={10}>
                    <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
                      <Button
                        icon={<UploadOutlined />}
                        type="primary"
                        onClick={() => setIsUploadModalOpen(true)}
                      >
                        Televerser des documents
                      </Button>
                    </Space>
                    <Table
                      rowKey="key"
                      columns={fournisseurFileColumns}
                      dataSource={normalizedFournisseurFiles}
                      className="custom-header1-table compact-rows-table"
                      bordered
                      size="small"
                      pagination={{ pageSize: 10, showSizeChanger: false }}
                      scroll={{ x: 'max-content' }}
                      locale={{ emptyText: 'Aucun fichier disponible pour ce fournisseur.' }}
                      components={{
                        body: {
                          cell: ({ style, ...rest }) => (
                            <td {...rest} style={{ ...style, padding: '2px 8px', lineHeight: 1.2 }} />
                          ),
                        },
                      }}
                    />
                  </Space>
                ),
              },
            ]}
          />
        </Card>

        {/* Modale prévisualisation facture numérisée */}
        <Modal
          open={previewVisible}
          title={
            <Space>
              <FilePdfOutlined style={{ color: '#ff4d4f' }} />
              {previewTitle}
            </Space>
          }
          onCancel={handleClosePreview}
          footer={null}
          width="80%"
          style={{ top: 20 }}
          styles={{ body: { padding: 0, height: '75vh' } }}
          destroyOnClose
        >
          {previewUrl && (
            <iframe
              src={previewUrl}
              title={previewTitle}
              width="100%"
              height="100%"
              style={{ border: 'none', display: 'block', borderRadius: '0 0 8px 8px' }}
            />
          )}
        </Modal>

        <Modal
          open={isUploadModalOpen}
          title="Televersement des documents administratifs"
          onCancel={() => { setIsUploadModalOpen(false); setSelectedFiles([]); }}
          onOk={async () => {
            const success = await handleUploadFournFiles();
            if (success) setIsUploadModalOpen(false);
          }}
          okText="Televerser"
          cancelText="Annuler"
          confirmLoading={uploading}
          width={600}
        >
          <Dragger
            multiple
            beforeUpload={() => false}
            fileList={selectedFiles}
            onChange={({ fileList }) => setSelectedFiles(fileList)}
            style={{ borderRadius: 10 }}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Cliquez ou glissez des fichiers dans cette zone</p>
            <p className="ant-upload-hint">Formats libres. Vous pouvez selectionner plusieurs fichiers.</p>
          </Dragger>
        </Modal>

      </Spin>
    </div>
  );
};

export default ViewFournisseurPage;


